/**
 * Tenant-config API client (GET /api/t/{tenantSlug}/v1/getTenantConfig).
 *
 * First frontend consumer of the endpoint (whose handler docstring
 * anticipated exactly this read path). Components source tenant-level
 * values from `tenants.config` (shape conventions: ADR-0009) through
 * this client instead of hardcoding tenant-specific values in product
 * code paths (CLAUDE.md commercial-product discipline).
 *
 * `config.features.*` keys are read with a code-side fallback and need
 * no ADR/migration. Setting a value for a tenant is operator data work
 * via a tools/recon/ trail (e.g.
 * tools/recon/directory-contact-email-peace-2026-07-02.sql).
 *
 * `working_states` sits beside `config`: the states the tenant works in,
 * derived by the server from its unit coverage, most covered counties
 * first. It is never stored in config, and the server leaves it out when
 * its read fails; readWorkingStates then gives [], and a new address has
 * no State to start on. `working_area` sits beside them too: the box the
 * maps open on, computed by the server from staff home points, read by
 * readWorkingArea (src/lib/mapView.ts), the whole country when missing.
 */

import { apiErrorFrom } from '@/lib/api';
import { isUsStateCode } from '@/lib/usStates';

export interface TenantConfigResponse {
  id: string;
  kind: 'practice' | 'mso';
  slug: string;
  display_name: string;
  /** tenants.config JSONB blob (brand, tile_set, features, prompts, ...). */
  config: Record<string, unknown>;
  /** The tenant's working states, read through readWorkingStates. */
  working_states?: unknown;
  /** The box every map opens on, read through readWorkingArea
   *  (src/lib/mapView.ts). */
  working_area?: unknown;
  /** The last day of the latest closed pay period, or null with no payroll
   *  calendar (notePayPeriod.ts payClosedThrough, LP-45). */
  pay_closed_through?: string | null;
  request_id: string;
}

/** A state the tenant works in, as getTenantConfig answers it. */
export interface WorkingState {
  code: string;
  name: string;
  county_count: number;
}

/** The response's working_states, in the server's order. Missing or not
 *  an array reads as []. An entry whose code is not a US state or
 *  territory code, whose name is blank, or whose county_count is not a
 *  whole number of at least 1 is dropped. Never re-sorted. */
export function readWorkingStates(response: TenantConfigResponse | undefined): WorkingState[] {
  const raw = response?.working_states;
  if (!Array.isArray(raw)) return [];
  const out: WorkingState[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { code, name, county_count } = entry as Record<string, unknown>;
    if (typeof code !== 'string' || !isUsStateCode(code)) continue;
    if (typeof name !== 'string' || name.trim() === '') continue;
    if (typeof county_count !== 'number' || !Number.isInteger(county_count) || county_count < 1) continue;
    out.push({ code, name: name.trim(), county_count });
  }
  return out;
}

export const tenantConfigApi = {
  async get(slug: string, signal?: AbortSignal): Promise<TenantConfigResponse> {
    const r = await fetch(`/api/t/${encodeURIComponent(slug)}/v1/getTenantConfig`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!r.ok) throw await apiErrorFrom(r, 'GET /getTenantConfig');
    return (await r.json()) as TenantConfigResponse;
  },
};

/**
 * `config.features.bake_off_league`: the Bake Off League switch. Only the
 * JSON boolean true turns it on (the server's readBakeOffLeague agrees).
 */
export function bakeOffLeagueOn(response: TenantConfigResponse | undefined): boolean {
  const features = (response?.config as Record<string, unknown> | undefined)?.['features'];
  return typeof features === 'object' && features !== null && (features as Record<string, unknown>)['bake_off_league'] === true;
}

/**
 * `config.features.directory_contact_email` - the operator mailbox that
 * receives documents to attach to directory listings (the
 * SuggestResourceForm attach-a-document hint). Returns null when unset
 * or malformed; callers hide the affordance rather than fall back to a
 * hardcoded address (there is no tenant-neutral email to fall back to).
 */
export function directoryContactEmail(
  response: TenantConfigResponse | undefined,
): string | null {
  return featuresEmail(response, 'directory_contact_email');
}

/**
 * `config.features.feedback_contact_email` - the mailbox behind the
 * AppShell user-menu Feedback item. Returns null when unset or
 * malformed; AppShell hides the menu entry rather than fall back to a
 * hardcoded address (there is no tenant-neutral email to fall back to).
 */
export function feedbackContactEmail(
  response: TenantConfigResponse | undefined,
): string | null {
  return featuresEmail(response, 'feedback_contact_email');
}

/**
 * `config.features.welcome_day` - the tenant's first day on Olive, a
 * `YYYY-MM-DD` string. On that day Home greets each browser once
 * (WelcomeMoment). Only a real calendar date in that exact shape reads;
 * anything else is null and the greeting never shows. A day in the past
 * is inert.
 */
export function welcomeDay(response: TenantConfigResponse | undefined): string | null {
  const features = response?.config?.['features'];
  if (typeof features !== 'object' || features === null) return null;
  const raw = (features as Record<string, unknown>)['welcome_day'];
  if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return raw;
}

/** The feedback link shared by the user menu and Home. */
export function feedbackMailto(email: string): string {
  return `mailto:${email}?subject=${encodeURIComponent('Olive feedback')}`;
}

function featuresEmail(
  response: TenantConfigResponse | undefined,
  key: string,
): string | null {
  const features = response?.config?.['features'];
  if (typeof features !== 'object' || features === null) return null;
  const email = (features as Record<string, unknown>)[key];
  if (typeof email !== 'string') return null;
  const trimmed = email.trim();
  return trimmed.includes('@') ? trimmed : null;
}

/**
 * `config.features.languages` (LP-33): the languages the Vitals form's
 * Primary language picker offers, in the tenant's order, before "Other".
 * A non-empty array of non-blank strings, each trimmed and de-duplicated
 * (case kept, first spelling wins); anything else falls back to
 * DEFAULT_LANGUAGES. The picker pre-selects "English" on a client with no
 * language yet, so a tenant list should carry it (the fallback does).
 */
export const DEFAULT_LANGUAGES: readonly string[] = [
  'English',
  'Spanish',
  'Chinese',
  'Korean',
  'Portuguese',
  'Tagalog',
  'Italian',
  'Russian',
  'Hindi',
  'Arabic',
];

export function readLanguages(response: TenantConfigResponse | undefined): readonly string[] {
  const features = response?.config?.['features'];
  if (typeof features !== 'object' || features === null) return DEFAULT_LANGUAGES;
  const raw = (features as Record<string, unknown>)['languages'];
  if (!Array.isArray(raw)) return DEFAULT_LANGUAGES;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'string') return DEFAULT_LANGUAGES;
    const trimmed = entry.trim();
    if (trimmed === '') return DEFAULT_LANGUAGES;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out.length === 0 ? DEFAULT_LANGUAGES : out;
}
